import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import { PUZZLE_MAX_GUESSES, PUZZLE_SPINS, STREAK_MILESTONE } from "@/lib/engagement-shared";
import { DAILY_FREE_PACKS } from "@/lib/game-constants";

const PalPuzzle = lazy(() => import("@/components/PalPuzzle").then((m) => ({ default: m.PalPuzzle })));
const StreakPanel = lazy(() => import("@/components/StreakPanel").then((m) => ({ default: m.StreakPanel })));

const FAQ: FaqItem[] = [
  {
    q: "Do I need an account to play the Palworld Pal Puzzle?",
    a: "No. The puzzle is on this page and fully playable as a guest — guesses, clues and the shareable result grid all work without signing in. A free Pal Stars account is only needed to collect the Palsino spins for a solve and to keep a login streak.",
  },
  {
    q: "What is the Palworld Pal Puzzle?",
    a: `The Palworld Pal Puzzle is a free daily guessing game on Pal Stars: guess the hidden Pal in ${PUZZLE_MAX_GUESSES} tries. Every guess compares its element, rarity, ride speed, capture rate, work suitability and habitat with the answer, so each row narrows the field. A new Pal is chosen at 00:00 UTC and everyone plays the same puzzle.`,
  },
  {
    q: "How do I win Palsino spins from the daily puzzle?",
    a: `Solve the puzzle while signed in to a free Pal Stars account and ${PUZZLE_SPINS} Palsino spins are credited automatically, once per account per UTC day. Visitors without an account can still play and share their result — the spins unlock when the account is created.`,
  },
  {
    q: "How does the Palworld daily login streak work?",
    a: `Check in once per UTC day and claim your streak reward. Day 1 pays 1 free booster pack and 2 Palsino spins, the reward grows every third consecutive day up to 5 packs and 10 spins, and every ${STREAK_MILESTONE}th day pays a milestone bonus of an extra 5 packs and 10 spins. Miss a day and the streak restarts at day 1, but your best streak is kept.`,
  },
  {
    q: "Do streak rewards stack with the free daily booster packs?",
    a: `Yes. Streak rewards are added on top of the ${DAILY_FREE_PACKS} free daily booster packs and your Palsino daily spins, and they land straight in your balance so you can open them immediately.`,
  },
  {
    q: "Can I cheat the daily Pal puzzle?",
    a: "No. The answer is resolved on the server and never sent to the browser until the puzzle is over, and the spin reward is banked by a single atomic database call, so a replayed or forged request cannot pay out twice.",
  },
  {
    q: "How do I share my Pal Puzzle result?",
    a: "Press Share your result when the puzzle ends. Pal Stars copies a spoiler-free emoji grid with your puzzle number and score, ready to post on X, TikTok, Discord or Reddit.",
  },
  {
    q: "When does the next Palworld Pal Puzzle unlock?",
    a: "At 00:00 UTC every day, together with your daily booster packs, Palsino spins and the streak check-in.",
  },
];

export const Route = createFileRoute("/daily-puzzle")({
  head: () => ({
    meta: [
      { title: "Palworld Pal Puzzle — Daily Game & Login Streak" },
      {
        name: "description",
        content: `Play the free Palworld Pal Puzzle: guess the daily Pal in ${PUZZLE_MAX_GUESSES} tries from element and mount clues, keep a login streak, win packs and spins.`,
      },
      { property: "og:title", content: "Palworld Pal Puzzle — guess the Pal of the day" },
      {
        property: "og:description",
        content: "A free daily Palworld guessing game with shareable results, login streaks and free booster pack rewards.",
      },
      { property: "og:url", content: "https://palstars.com/daily-puzzle" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/daily-puzzle" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Pal Puzzle", "/daily-puzzle"]]),
      faqSchema(FAQ, "https://palstars.com/daily-puzzle"),
    ],
  }),
  component: DailyPuzzlePage,
});

function DailyPuzzlePage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Daily game</p>
        <h1 className="mt-2 text-3xl font-bold">Palworld Pal Puzzle & daily streak</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Palworld Pal Puzzle is a free daily guessing game: name the hidden Pal in {PUZZLE_MAX_GUESSES} tries using
          element, rarity, ride speed, capture rate, work and habitat clues. Solving it credits {PUZZLE_SPINS} Palsino
          spins, and claiming your daily login streak pays free booster packs that grow the longer you keep it alive. The
          puzzle itself needs no account — sign in only to collect the spins.
        </p>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palworld Pal Puzzle</h2><p className="mt-2 text-sm text-muted-foreground">Loading today's puzzle…</p></section>}>
        <PalPuzzle />
      </Suspense>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Daily login streak</h2><p className="mt-2 text-sm text-muted-foreground">Loading your streak…</p></section>}>
        <StreakPanel />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Keep the run going</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link to="/weekly-tournament" className="font-semibold text-primary hover:underline">Weekly arcade tournament →</Link>{" "}
            three featured Palworld games every week, free to enter.
          </li>
          <li>
            <Link to="/leaderboard" className="font-semibold text-primary hover:underline">Weekly leaderboard →</Link>{" "}
            best pulls, jackpots and puzzle days of the last 7 days.
          </li>
          <li>
            <Link to="/palsino" className="font-semibold text-primary hover:underline">Pal Stars Palsino →</Link> spend
            your puzzle and streak spins on the reels.
          </li>
          <li>
            <Link to="/pack-opening" className="font-semibold text-primary hover:underline">Pack opening game →</Link>{" "}
            open the packs your streak paid out.
          </li>
          <li>
            <Link to="/pals" className="font-semibold text-primary hover:underline">Palworld Pal database →</Link> study
            elements, mounts and capture rates to solve faster.
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld Pal Puzzle FAQ" />
    </div>
  );
}
